import { ChangeDetectorRef, Component, OnInit } from '@angular/core';
import { NgxSmartModalService } from 'ngx-smart-modal';
import { MediaTranscoderHistoryViewModel } from './history.view-model';
import { JobReportViewModel } from './transcoding-job-report/job-report.view-model';
import {
  ETranscodingJobPriority,
  ETranscodingJobStatus,
} from '../../../../domain/media-transcoder/transcoder-job';
import { HistoryTranscoderJobViewModel } from './history-transcoder-job-view.model';
import { IMediaTranscoderRepository } from '../../../../domain/media-transcoder/media-transcoder.repository';
import { IMediaTranscoderJobsViewModelApi } from '../transcoder-job.view-model.api';
import { EMediaTranscodingItemStatus } from '../../../../domain/media-transcoder/transcoding-item';
import moment from 'moment';
import { IRouterProxy } from '../job-settings/job-settings.view-model';
import { ActivatedRoute, Router } from '@angular/router';
import { MediaTranscodingItem } from '../../../../domain/media-transcoder/media-transcoding-item';
import { MediaRepository } from '../../../../domain/media-repository';
import { ToastrService } from 'ngx-toastr';
import { TranscodingBulkRedoTracker } from '../../../../domain/media-transcoder/transcoding-bulk-redo-tracker';

@Component({
  selector: 'evrideo-history',
  templateUrl: './history.component.html',
  styleUrls: ['./history.component.scss'],
})
export class MediaTranscoderHistoryComponent implements OnInit {
  /** Shown in the report modal while job report data is being fetched. */
  readonly reportLoadingMessage =
    'Retrieving transcoding job report. Please wait.';

  vm: MediaTranscoderHistoryViewModel;
  constructor(
    private _changeDetectorRef: ChangeDetectorRef,
    private _mediaTranscoderRepository: IMediaTranscoderRepository,
    private _ngxSmartModalService: NgxSmartModalService,
    router: Router,
    private _route: ActivatedRoute,
    private _mediaRepository: MediaRepository,
    private _toastr: ToastrService,
  ) {
    this.vm = new MediaTranscoderHistoryViewModel(
      <IMediaTranscoderJobsViewModelApi>{
        updateDOM() {
          _changeDetectorRef.detectChanges();
        },
        getReport(jobId: string): Promise<JobReportViewModel[]> {
          return _mediaTranscoderRepository
            .getTranscodingJobDetails(jobId)
            .then((jobDetails) => jobDetails.transcodingItems)
            .then((transcodingItems) =>
              transcodingItems
                .map((item) => item as MediaTranscodingItem)
                .map(
                  (transcodingItem) =>
                    new JobReportViewModel(
                      transcodingItem.videoTenantUniqueId,
                      transcodingItem.mediaId,
                      transcodingItem.lastStatusTime.format(
                        'YYYY-MM-DD HH:mm:ss',
                      ),
                      EMediaTranscodingItemStatus[transcodingItem.status],
                      transcodingItem.resultUrl,
                    ),
                ),
            );
        },
        async getJobs(): Promise<HistoryTranscoderJobViewModel[]> {
          const allDestinations =
            await _mediaTranscoderRepository.getTenantTranscodingDestinations();
          const allDestinationsDictionary = Object.assign(
            {},
            ...allDestinations.map((s) => ({ [s.id]: s.name })),
          );

          const [historyJobs, activeJobs] = await Promise.all([
            _mediaTranscoderRepository.getJobs([
              ETranscodingJobStatus.History,
            ]),
            _mediaTranscoderRepository.getJobs([
              ETranscodingJobStatus.Active,
            ]),
          ]);

          return historyJobs
            .sort((a, b) =>
              a.actualEndTime.diff(b.actualEndTime) > 0 ? -1 : 1,
            )
            .map((job) => {
              const viewModel = new HistoryTranscoderJobViewModel(
                job.id,
                job.name,
                allDestinationsDictionary[job.destinationId],
                job.actualStartTime.format('YYYY-MM-DD HH:mm:ss'),
                job.actualEndTime.format('YYYY-MM-DD HH:mm:ss'),
                job.estimatedEndTime.format('YYYY-MM-DD HH:mm:ss'),
                moment
                  .utc(job.actualEndTime.diff(job.estimatedStartTime))
                  .format('HH:mm:ss'),
                ETranscodingJobStatus[job.status].toString(),
                ETranscodingJobPriority[job.priority].toString(),
                job.itemStatuses,
                job.transcodingProfileId,
                job.sideEffectId ?? 0,
              );

              const redoLink =
                TranscodingBulkRedoTracker.getLatestLinkForSourceJob(job.id);
              if (redoLink) {
                viewModel.assignRedo(
                  redoLink.redoJobId,
                  redoLink.redoJobName,
                  TranscodingBulkRedoTracker.resolveRedoJobState(
                    redoLink.redoJobId,
                    activeJobs,
                    historyJobs,
                  ),
                );
              }

              return viewModel;
            });
        },
      },
      <IRouterProxy>{
        navigateTo(path: string, isRelativeToCurrentRoute: boolean) {
          if (isRelativeToCurrentRoute) {
            router.navigate([path], { relativeTo: _route });
          } else {
            router.navigate([path]);
          }
        },
        locationBack() {
          window.history.back();
        },
      },
      this._mediaRepository,
      this._toastr,
    );
  }

  async ngOnInit() {
    await this.vm.refreshJobs();
  }

  openBulkRedoDialog(): void {
    this._ngxSmartModalService.getModal('historyBulkRedoModal').open();
  }

  async onBulkRedoCompleted(): Promise<void> {
    await this.vm.refreshJobs();
    this._changeDetectorRef.detectChanges();
  }
}
